import { Metadata } from 'next';
import { ReactNode } from 'react';

import {
    FaqConfig,
    MismatchConfig,
    UpsellItemsConfiguration,
    UpsellModalComponentType,
} from '@app-types/eligibility/config';
import { ExtraCmsBlockByTab, FaiTabsEnum } from '@app-types/home-fai';
import { PACK_HASH } from '@app-types/home-pack';
import { PageViewTrackingType } from '@app-types/tracking';

import { globalFaiUpsellConfig } from '@helpers/fai-upsell-config';
import { LegalsSlugType, PAGE_SLUG, partials } from '@helpers/partials';

import { PlanTypeId } from '@constants/plan';
import { CATEGORY_TYPE, ELIGIBILITY_JOURNEY, PLAY, TECHNOLOGY } from '@constants/provider';

export class EligibilityJourneyConfig {
    protected readonly name: ELIGIBILITY_JOURNEY = ELIGIBILITY_JOURNEY.DEFAULT;
    protected readonly seo: Metadata = {
        title: "Test d'éligibilité",
        robots: 'noindex, nofollow',
    };
    protected readonly tracking: PageViewTrackingType = {
        page: 'Eligibilite_test',
        pageCategory: 'Eligibilite',
        eCommerceStep: '',
        universe: 'Boutique_fai',
        marketLine: 'Fai',
        variant: {
            type_offres: 'Offres_Box',
        },
    };
    protected allowBox4g: boolean = false;
    protected fallbackMismatchOffers: PlanTypeId | null = null;
    protected readonly offerCategoryCodeToShow: PlanTypeId = PlanTypeId.FAI_ELIGIBILITE;
    protected readonly offerCategoryCodeToHide: PlanTypeId[] = [];
    protected readonly category: CATEGORY_TYPE = CATEGORY_TYPE.GP;
    protected readonly groupOffersBy: string[] = ['rangeNg', 'commercialSeries', 'play'];
    protected readonly showPackTabs: boolean = true;
    protected readonly showComparatorSelection: boolean = true;
    protected readonly showDetailsButton: boolean = true;
    protected readonly extraCmsBlockByTab?: ExtraCmsBlockByTab = {
        [FaiTabsEnum.BOX_INTERNET]: partials.MARKETING.FAI_EXTRA_BBOX_PACK,
    };
    protected readonly shouldCheckCommercialPartner: boolean = false;
    protected readonly accessKeyPartnerConfigKey?: 'lmPartner' | 'promoPriveeReferrers';
    protected readonly showComboProductSlug?: PACK_HASH[];
    protected readonly hideForecast?: boolean = false;
    protected readonly isSingleOfferWording: boolean = false;
    protected readonly eligBlock: {
        hideEligibilityTypeSelector: boolean;
        eligibilityCtaLabel: string;
        hideTlvRecall?: boolean;
    } = {
        hideEligibilityTypeSelector: false,
        eligibilityCtaLabel: 'Tester mon éligibilité',
        hideTlvRecall: false,
    };

    protected readonly wordings: {
        mainTitle: ReactNode;
        overrideShortListTitle?: boolean;
        subtitle?: ReactNode;
        noFiber: ReactNode;
    } = {
        mainTitle: 'On regarde les offres disponibles chez vous\u00A0?',
        noFiber: "En attendant l'arrivée de la fibre...",
    };
    protected readonly mismatch?: MismatchConfig = {
        title: 'Zut\u00A0! Ce logement ne semble éligible à aucune offre',
        showAddress: true,
        icon: 'tri-frown',
    };
    protected readonly faq: FaqConfig = {
        title: 'Vos questions fréquentes sur l’éligibilité à la fibre',
        path: PAGE_SLUG.ELIGIBILITY,
        isHiddenBeforeTest: false,
    };
    protected readonly legalsPath: LegalsSlugType = PAGE_SLUG.ELIGIBILITY;
    protected readonly productsToUpsell: UpsellItemsConfiguration[] = [
        {
            baseProduct: { rangeNg: 'xgbox', commercialSeries: 'medium_xgbox' },
            upselledProduct: { rangeNg: 'xgbox', commercialSeries: 'medium_tv_xgbox' },
            modalComponentType: UpsellModalComponentType.CLASSIC,
        },
        {
            baseProduct: { rangeNg: 'must', commercialSeries: 'pack_solo', play: PLAY._3P, isHiddenDefault: true },
            upselledProduct: { rangeNg: 'must', commercialSeries: 'pack_solo', play: PLAY._2P },
            modalComponentType: UpsellModalComponentType.DOWNGRADE_MUST_PLAY,
        },
    ];

    protected readonly technologies?: TECHNOLOGY[];
    protected readonly referrerConfigKey?: string;
    protected readonly cookieKey?: string;
    protected readonly showOffersWording?: string;

    public getConfig() {
        return {
            name: this.name,
            wordings: this.wordings,
            seo: this.seo,
            tracking: this.tracking,
            allowBox4g: this.allowBox4g,
            offerCategoryCodeToShow: this.offerCategoryCodeToShow,
            fallbackMismatchOffers: this.fallbackMismatchOffers,
            offerCategoryCodeToHide: this.offerCategoryCodeToHide,
            category: this.category,
            groupOffersBy: this.groupOffersBy,
            showPackTabs: this.showPackTabs,
            showComparatorSelection: this.showComparatorSelection,
            showComboProductSlug: this.showComboProductSlug,
            hideForecast: this.hideForecast,
            isSingleOfferWording: this.isSingleOfferWording,
            extraCmsBlockByTab: this.extraCmsBlockByTab,
            eligBlock: this.eligBlock,
            mismatch: this.mismatch,
            faq: this.faq,
            legalsPath: this.legalsPath,
            productsToUpsell: [...globalFaiUpsellConfig, ...this.productsToUpsell],
            technologies: this.technologies,
            referrerConfigKey: this.referrerConfigKey,
            cookieKey: this.cookieKey,
            shouldCheckCommercialPartner: this.shouldCheckCommercialPartner,
            accessKeyPartnerConfigKey: this.accessKeyPartnerConfigKey,
            showDetailsButton: this.showDetailsButton,
            showOffersWording: this.showOffersWording,
        };
    }
}
